import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ConversationViewModel } from '../types/conversation/ConversationViewModel';
import { apiClient } from '../client/client';

/**
 * Query params for GET /api/conversation (paginated list).
 */
export interface ConversationListParams {
  ListingId?: string;
  SenderId?: string;
  ReceiverId?: string;
  'Pagination.PageSize'?: number;
  'Pagination.PageNumber'?: number;
}

/**
 * Conversation service – contact seller, list conversations, close conversation.
 */
export class ConversationService {
  private static readonly BASE = '/api/conversation';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  /**
   * POST /api/listing/{listingId}/contact-seller
   * Creates or returns existing conversation for this listing; returns ConversationViewModel.
   */
  async contactSeller(listingId: string): Promise<ServiceResultT<ConversationViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ConversationViewModel>>(
      `/api/listing/${listingId}/contact-seller`
    );
    return data;
  }

  /**
   * POST /api/conversation/{id}/close
   */
  async closeConversation(conversationId: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${ConversationService.BASE}/${conversationId}/close`
    );
    return data;
  }

  /**
   * GET /api/conversation – paginated list with optional filters.
   */
  async getConversations(
    params?: ConversationListParams
  ): Promise<ServiceResultT<ConversationViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<ConversationViewModel[]>>(
      ConversationService.BASE,
      { params: params ?? {} }
    );
    return data;
  }

  /**
   * GET /api/conversation/{id} – single conversation (e.g. with messages).
   */
  async getById(conversationId: string): Promise<ServiceResultT<ConversationViewModel>> {
    const { data } = await this.client.get<ServiceResultT<ConversationViewModel>>(
      `${ConversationService.BASE}/${conversationId}`
    );
    return data;
  }
}

export const conversationService = new ConversationService();
